var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { treeLayout } from '../layout/index';
import { seededRandom } from './helpers';
/** Physics-based force-directed layout with seeded reproducibility. */
export function forceDirectedLayout(nodes, edges, options) {
    var e_1, _a, e_2, _b, e_3, _c, e_4, _d, e_5, _e;
    var _f, _g;
    if (options === void 0) { options = {}; }
    var _h = options.width, width = _h === void 0 ? 600 : _h, _j = options.height, height = _j === void 0 ? 400 : _j, _k = options.iterations, iterations = _k === void 0 ? 100 : _k, _l = options.seed, seed = _l === void 0 ? 42 : _l, _m = options.repulsion, repulsion = _m === void 0 ? 4000 : _m, _o = options.attraction, attraction = _o === void 0 ? 0.05 : _o, _p = options.damping, damping = _p === void 0 ? 0.85 : _p;
    var rand = seededRandom(seed);
    var positions = new Map();
    try {
        for (var nodes_1 = __values(nodes), nodes_1_1 = nodes_1.next(); !nodes_1_1.done; nodes_1_1 = nodes_1.next()) {
            var n = nodes_1_1.value;
            positions.set(n.id, {
                x: (_f = n.x) !== null && _f !== void 0 ? _f : rand() * width,
                y: (_g = n.y) !== null && _g !== void 0 ? _g : rand() * height,
                vx: 0,
                vy: 0,
            });
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (nodes_1_1 && !nodes_1_1.done && (_a = nodes_1.return)) _a.call(nodes_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    for (var iter = 0; iter < iterations; iter++) {
        var forces = new Map();
        try {
            for (var nodes_2 = (e_2 = void 0, __values(nodes)), nodes_2_1 = nodes_2.next(); !nodes_2_1.done; nodes_2_1 = nodes_2.next()) {
                var n = nodes_2_1.value;
                forces.set(n.id, { fx: 0, fy: 0 });
            }
        }
        catch (e_2_1) { e_2 = { error: e_2_1 }; }
        finally {
            try {
                if (nodes_2_1 && !nodes_2_1.done && (_b = nodes_2.return)) _b.call(nodes_2);
            }
            finally { if (e_2) throw e_2.error; }
        }
        // Repulsion between all pairs
        for (var i = 0; i < nodes.length; i++) {
            for (var j = i + 1; j < nodes.length; j++) {
                var a = positions.get(nodes[i].id);
                var b = positions.get(nodes[j].id);
                var dx = a.x - b.x;
                var dy = a.y - b.y;
                var dist = Math.sqrt(dx * dx + dy * dy);
                if (dist < 1) {
                    dx = rand() - 0.5;
                    dy = rand() - 0.5;
                    dist = 1;
                }
                var force = repulsion / (dist * dist);
                var fx = (dx / dist) * force;
                var fy = (dy / dist) * force;
                forces.get(nodes[i].id).fx += fx;
                forces.get(nodes[i].id).fy += fy;
                forces.get(nodes[j].id).fx -= fx;
                forces.get(nodes[j].id).fy -= fy;
            }
        }
        try {
            // Attraction along edges
            for (var edges_1 = (e_3 = void 0, __values(edges)), edges_1_1 = edges_1.next(); !edges_1_1.done; edges_1_1 = edges_1.next()) {
                var edge = edges_1_1.value;
                var a = positions.get(edge.from);
                var b = positions.get(edge.to);
                if (!a || !b)
                    continue;
                var dx = b.x - a.x;
                var dy = b.y - a.y;
                var dist = Math.sqrt(dx * dx + dy * dy) || 1;
                var force = dist * attraction;
                var fx = (dx / dist) * force;
                var fy = (dy / dist) * force;
                forces.get(edge.from).fx += fx;
                forces.get(edge.from).fy += fy;
                forces.get(edge.to).fx -= fx;
                forces.get(edge.to).fy -= fy;
            }
        }
        catch (e_3_1) { e_3 = { error: e_3_1 }; }
        finally {
            try {
                if (edges_1_1 && !edges_1_1.done && (_c = edges_1.return)) _c.call(edges_1);
            }
            finally { if (e_3) throw e_3.error; }
        }
        // Apply forces with damping and center gravity
        var cx = width / 2;
        var cy = height / 2;
        try {
            for (var nodes_3 = (e_4 = void 0, __values(nodes)), nodes_3_1 = nodes_3.next(); !nodes_3_1.done; nodes_3_1 = nodes_3.next()) {
                var n = nodes_3_1.value;
                var p = positions.get(n.id);
                var f = forces.get(n.id);
                f.fx += (cx - p.x) * 0.01;
                f.fy += (cy - p.y) * 0.01;
                p.vx = (p.vx + f.fx) * damping;
                p.vy = (p.vy + f.fy) * damping;
                p.x = Math.max(20, Math.min(width - 20, p.x + p.vx));
                p.y = Math.max(20, Math.min(height - 20, p.y + p.vy));
            }
        }
        catch (e_4_1) { e_4 = { error: e_4_1 }; }
        finally {
            try {
                if (nodes_3_1 && !nodes_3_1.done && (_d = nodes_3.return)) _d.call(nodes_3);
            }
            finally { if (e_4) throw e_4.error; }
        }
    }
    var result = new Map();
    try {
        for (var nodes_4 = __values(nodes), nodes_4_1 = nodes_4.next(); !nodes_4_1.done; nodes_4_1 = nodes_4.next()) {
            var n = nodes_4_1.value;
            var p = positions.get(n.id);
            result.set(n.id, { x: p.x, y: p.y });
        }
    }
    catch (e_5_1) { e_5 = { error: e_5_1 }; }
    finally {
        try {
            if (nodes_4_1 && !nodes_4_1.done && (_e = nodes_4.return)) _e.call(nodes_4);
        }
        finally { if (e_5) throw e_5.error; }
    }
    return result;
}
/** Radial layout for mind maps — children arranged in a circle around center. */
export function radialLayout(group, cx, cy, innerRadius, outerRadius) {
    var children = group.children;
    if (children.length === 0)
        return;
    // First child is center
    if (children.length === 1) {
        children[0].x = cx;
        children[0].y = cy;
        children[0].markDirty();
        return;
    }
    children[0].x = cx;
    children[0].y = cy;
    children[0].markDirty();
    var outer = children.slice(1);
    var n = outer.length;
    for (var i = 0; i < n; i++) {
        var angle = (2 * Math.PI * i) / n - Math.PI / 2;
        var r = n <= 4 ? innerRadius : outerRadius;
        outer[i].x = cx + r * Math.cos(angle) - outer[i].getBounds().width / 2;
        outer[i].y = cy + r * Math.sin(angle) - outer[i].getBounds().height / 2;
        outer[i].markDirty();
    }
}
/**
 * Mermaid-style mind map: root in center, branches split left/right,
 * leaves stacked outward from each branch.
 */
export function mindMapLayout(group, canvasW, canvasH) {
    var nodes = group.children.filter(function (c) { var _a; return !((_a = c.metadata) === null || _a === void 0 ? void 0 : _a.diagramEdgeLayer); });
    if (nodes.length === 0)
        return;
    var center = nodes[0];
    var branches = nodes.slice(1);
    var cx = canvasW / 2;
    var cy = canvasH / 2;
    var cW = 140;
    var cH = 52;
    center.x = cx - cW / 2;
    center.y = cy - cH / 2;
    center.markDirty();
    var gapFromCenter = Math.max(56, Math.min(100, canvasW * 0.1));
    var branchGapY = 56;
    var leafGapY = 8;
    var leafIndent = 36;
    var mid = Math.ceil(branches.length / 2);
    var left = branches.slice(0, mid);
    var right = branches.slice(mid);
    var placeColumn = function (col, side) {
        var e_6, _a;
        var totalH = col.reduce(function (sum, b, i) {
            var leaves = b.children.filter(function (c) { var _a; return (_a = c.metadata) === null || _a === void 0 ? void 0 : _a.diagramId; });
            var leafStack = leaves.length > 0 ? leaves.length * 30 + (leaves.length - 1) * leafGapY : 0;
            var h = Math.max(40, leafStack);
            return sum + h + (i > 0 ? branchGapY : 0);
        }, 0);
        var y = cy - totalH / 2;
        var _loop_1 = function (branch) {
            branch.metadata.mindSide = side;
            var leaves = branch.children.filter(function (c) { var _a; return (_a = c.metadata) === null || _a === void 0 ? void 0 : _a.diagramId; });
            var leafH = 30;
            var leafW = 96;
            var leafStackH = leaves.length > 0 ? leaves.length * leafH + (leaves.length - 1) * leafGapY : 0;
            var branchH = 40;
            var slotH = Math.max(branchH, leafStackH);
            var bW = 112;
            if (side === 'right') {
                branch.x = cx + cW / 2 + gapFromCenter;
            }
            else {
                branch.x = cx - cW / 2 - gapFromCenter - bW;
            }
            branch.y = y + (slotH - branchH) / 2;
            branch.markDirty();
            if (leaves.length > 0) {
                var ly_1 = (branchH - leafStackH) / 2;
                leaves.forEach(function (leaf) {
                    if (side === 'right') {
                        leaf.x = bW + leafIndent;
                    }
                    else {
                        leaf.x = -(leafW + leafIndent);
                    }
                    leaf.y = ly_1;
                    leaf.markDirty();
                    ly_1 += leafH + leafGapY;
                });
            }
            y += slotH + branchGapY;
        };
        try {
            for (var col_1 = __values(col), col_1_1 = col_1.next(); !col_1_1.done; col_1_1 = col_1.next()) {
                var branch = col_1_1.value;
                _loop_1(branch);
            }
        }
        catch (e_6_1) { e_6 = { error: e_6_1 }; }
        finally {
            try {
                if (col_1_1 && !col_1_1.done && (_a = col_1.return)) _a.call(col_1);
            }
            finally { if (e_6) throw e_6.error; }
        }
    };
    placeColumn(left, 'left');
    placeColumn(right, 'right');
}
/** Auto-layout diagram nodes in a tree structure */
export function layoutDiagram(group, levelGap, siblingGap) {
    if (levelGap === void 0) { levelGap = 80; }
    if (siblingGap === void 0) { siblingGap = 40; }
    treeLayout(group, levelGap, siblingGap);
}
/** Horizontal pipeline layout — stages in a row */
export function pipelineLayout(group, gap, padding, canvasH) {
    var e_7, _a;
    var _b, _c;
    if (gap === void 0) { gap = 40; }
    if (padding === void 0) { padding = 10; }
    var stageH = 50;
    var x = padding;
    var y = canvasH ? Math.max(padding, (canvasH - stageH) / 2) : padding;
    try {
        for (var _d = __values(group.children), _e = _d.next(); !_e.done; _e = _d.next()) {
            var child = _e.value;
            if (((_b = child.metadata) === null || _b === void 0 ? void 0 : _b.diagramId) === undefined && ((_c = child.metadata) === null || _c === void 0 ? void 0 : _c.pipelineStatus) === undefined) {
                continue;
            }
            child.x = x;
            child.y = y;
            child.markDirty();
            x += 118 + gap;
        }
    }
    catch (e_7_1) { e_7 = { error: e_7_1 }; }
    finally {
        try {
            if (_e && !_e.done && (_a = _d.return)) _a.call(_d);
        }
        finally { if (e_7) throw e_7.error; }
    }
}
/** Position diagram nodes from data using force layout */
export function layoutNodesForce(nodes, edges, options) {
    return forceDirectedLayout(nodes.map(function (n) { return ({ id: n.id, x: n.x, y: n.y }); }), edges.map(function (e) { return ({ from: e.from, to: e.to }); }), options);
}
